import { useMemo } from "react";
import { useGetIdentity, useGetList, useTranslate } from "ra-core";
import { useSearchParams } from "react-router";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { leadTypeTranslationKey } from "../deals/leadTypes";
import {
  ListEmptyState,
  ListErrorState,
  ListRowsSkeleton,
} from "../design/ListStates";
import { ProgressBar } from "../design/ProgressBar";
import { useConfigurationContext } from "../root/ConfigurationContext";
import {
  LEAD_TYPES,
  type AppRole,
  type FunnelMetrics,
  type FunnelMetricsFilters,
  type FunnelStageMetric,
  type LeadType,
  type Project,
  type Sale,
} from "../types";
import { buildFunnelBottleneckNarrative } from "./funnelNarrative";
import { useFunnelMetrics } from "./useFunnelMetrics";

/**
 * Almikia Epic ER (TASK-004, vision #13): the Dirección funnel section --
 * per-stage conversion and time-in-stage, first-response time and
 * contactability (direccion only), sliceable by project/lead type (direccion
 * and lead_manager) and by a single advisor (direccion only, never a
 * per-advisor comparison -- Independence guide), plus a one-sentence
 * bottleneck narrative computed by rules (`funnelNarrative.ts`). Mounted from
 * `DireccionPanel.tsx`, extending it without replacing any existing section.
 * Every figure comes from `get_funnel_metrics()` (TASK-002), already scoped
 * by the caller's own RLS/FakeRest emulation -- this component never fetches
 * a full lead list. Filter state lives in the URL search params (below), so
 * a shared link/refresh keeps the same slice.
 */

const MAX_PROJECTS = 50;
const MAX_ADVISOR_CHOICES = 200;
const ALL_VALUE = "__all__";

const PROJECT_PARAM = "funnel_project";
const LEAD_TYPE_PARAM = "funnel_lead_type";
const ADVISOR_PARAM = "funnel_advisor";

type Translate = ReturnType<typeof useTranslate>;

function parseIdParam(value: string | null): number | undefined {
  if (!value) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/** "20.5 h" under 48h, "3.2 d" from 48h on -- tabular numbers, no pluralization needed for an abbreviated unit. */
function formatHoursDuration(
  hours: number | null,
  translate: Translate,
): string {
  if (hours == null) {
    return translate("crm.dashboard.direccion.funnel.no_data");
  }
  return hours < 48
    ? translate("crm.dashboard.direccion.funnel.time_hours", { hours })
    : translate("crm.dashboard.direccion.funnel.time_days", {
        days: Math.round((hours / 24) * 10) / 10,
      });
}

function FunnelSelectFilter({
  label,
  value,
  placeholder,
  options,
  onChange,
}: {
  label: string;
  value: string;
  placeholder: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex min-w-40 flex-col gap-1">
      <span className="text-sm font-medium text-muted-foreground">{label}</span>
      <Select
        value={value || ALL_VALUE}
        onValueChange={(next) => onChange(next === ALL_VALUE ? "" : next)}
      >
        <SelectTrigger className="h-11 w-full" aria-label={label}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_VALUE}>{placeholder}</SelectItem>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function FunnelStageRow({
  label,
  stage,
  maxDurationHours,
  translate,
}: {
  label: string;
  stage: FunnelStageMetric;
  maxDurationHours: number;
  translate: Translate;
}) {
  const durationPercent =
    stage.median_hours_in_stage != null && maxDurationHours > 0
      ? (stage.median_hours_in_stage / maxDurationHours) * 100
      : 0;

  return (
    <div className="flex flex-col gap-2 rounded-2xl bg-card p-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-base font-bold">{label}</span>
        <span className="shrink-0 text-sm tabular-nums text-muted-foreground">
          {translate("crm.dashboard.direccion.funnel.leads_count", {
            smart_count: stage.reached_count,
          })}
        </span>
      </div>

      <div className="flex flex-col gap-1">
        <span className="text-sm text-muted-foreground">
          {translate("crm.dashboard.direccion.funnel.conversion_label")}
        </span>
        <ProgressBar
          value={stage.conversion_rate ?? 0}
          max={100}
          label={translate("crm.dashboard.direccion.funnel.conversion_label")}
        />
        <span className="text-sm tabular-nums">
          {stage.conversion_rate != null
            ? translate("crm.dashboard.direccion.funnel.conversion_percent", {
                percent: stage.conversion_rate,
              })
            : translate("crm.dashboard.direccion.funnel.no_data")}
        </span>
      </div>

      <div className="flex flex-col gap-1">
        <span className="text-sm text-muted-foreground">
          {translate("crm.dashboard.direccion.funnel.time_in_stage_label")}
        </span>
        <ProgressBar
          value={durationPercent}
          max={100}
          label={translate(
            "crm.dashboard.direccion.funnel.time_in_stage_label",
          )}
        />
        <span className="text-sm tabular-nums">
          {formatHoursDuration(stage.median_hours_in_stage, translate)}
        </span>
      </div>
    </div>
  );
}

/** Direccion-only (Independence guide: advisor activity metrics are never visible to any other role). */
function DireccionOnlyMetrics({
  metrics,
  translate,
}: {
  metrics: FunnelMetrics;
  translate: Translate;
}) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <div className="flex flex-col gap-1 rounded-2xl bg-card p-3">
        <span className="text-sm text-muted-foreground">
          {translate("crm.dashboard.direccion.funnel.first_response_title")}
        </span>
        <span className="text-lg font-bold tabular-nums">
          {formatHoursDuration(metrics.first_response_median_hours, translate)}
        </span>
      </div>
      <div className="flex flex-col gap-1 rounded-2xl bg-card p-3">
        <span className="text-sm text-muted-foreground">
          {translate("crm.dashboard.direccion.funnel.contactability_title")}
        </span>
        <span className="text-lg font-bold tabular-nums">
          {metrics.contactability_rate != null
            ? translate(
                "crm.dashboard.direccion.funnel.contactability_percent",
                {
                  percent: metrics.contactability_rate,
                },
              )
            : translate("crm.dashboard.direccion.funnel.no_data")}
        </span>
      </div>
    </div>
  );
}

export const DireccionFunnelSection = () => {
  const translate = useTranslate();
  const { identity } = useGetIdentity();
  const role = identity?.role as AppRole | undefined;
  const isDireccion = role === "direccion";
  const { dealStages } = useConfigurationContext();
  const stageLabelByKey = useMemo(
    () => new Map(dealStages.map((stage) => [stage.value, stage.label])),
    [dealStages],
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const projectParam = searchParams.get(PROJECT_PARAM);
  const leadTypeParam = searchParams.get(LEAD_TYPE_PARAM);
  const advisorParam = isDireccion ? searchParams.get(ADVISOR_PARAM) : null;

  const setParam = (key: string, value: string) => {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (value) {
          next.set(key, value);
        } else {
          next.delete(key);
        }
        return next;
      },
      { replace: true },
    );
  };

  const projectsResult = useGetList<Project>("projects", {
    pagination: { page: 1, perPage: MAX_PROJECTS },
    sort: { field: "name", order: "ASC" },
    filter: { "archived@eq": false },
  });
  const advisorsResult = useGetList<Sale>(
    "sales",
    {
      pagination: { page: 1, perPage: MAX_ADVISOR_CHOICES },
      sort: { field: "last_name", order: "ASC" },
      filter: { role: "asesora" },
    },
    { enabled: isDireccion },
  );

  const filters: FunnelMetricsFilters = useMemo(
    () => ({
      projectId: parseIdParam(projectParam) ?? null,
      leadType: (leadTypeParam as LeadType | null) ?? null,
      advisorId: isDireccion ? (parseIdParam(advisorParam) ?? null) : null,
    }),
    [projectParam, leadTypeParam, advisorParam, isDireccion],
  );

  const {
    data: metrics,
    isPending,
    error,
    refetch,
  } = useFunnelMetrics(filters);

  const narrative = useMemo(
    () =>
      metrics ? buildFunnelBottleneckNarrative(metrics, stageLabelByKey) : null,
    [metrics, stageLabelByKey],
  );

  const maxDurationHours = useMemo(() => {
    if (!metrics) return 0;
    return metrics.stages.reduce(
      (max, stage) => Math.max(max, stage.median_hours_in_stage ?? 0),
      0,
    );
  }, [metrics]);

  const isEmpty =
    !!metrics && metrics.stages.every((stage) => stage.reached_count === 0);

  const projectOptions = (projectsResult.data ?? []).map((project) => ({
    value: String(project.id),
    label: project.name,
  }));
  const leadTypeOptions = LEAD_TYPES.map((leadType) => ({
    value: leadType,
    label: translate(leadTypeTranslationKey(leadType)),
  }));
  const advisorOptions = (advisorsResult.data ?? []).map((advisor) => ({
    value: String(advisor.id),
    label: `${advisor.first_name} ${advisor.last_name}`,
  }));

  return (
    <section className="flex flex-col gap-4">
      <h2 className="text-xl font-semibold text-muted-foreground">
        {translate("crm.dashboard.direccion.funnel.title")}
      </h2>

      <div className="flex flex-wrap gap-3">
        <FunnelSelectFilter
          label={translate(
            "crm.dashboard.direccion.funnel.project_filter_label",
          )}
          placeholder={translate(
            "crm.dashboard.direccion.funnel.filter_all_option",
          )}
          value={projectParam ?? ""}
          options={projectOptions}
          onChange={(value) => setParam(PROJECT_PARAM, value)}
        />
        <FunnelSelectFilter
          label={translate(
            "crm.dashboard.direccion.funnel.lead_type_filter_label",
          )}
          placeholder={translate(
            "crm.dashboard.direccion.funnel.filter_all_option",
          )}
          value={leadTypeParam ?? ""}
          options={leadTypeOptions}
          onChange={(value) => setParam(LEAD_TYPE_PARAM, value)}
        />
        {isDireccion ? (
          <FunnelSelectFilter
            label={translate(
              "crm.dashboard.direccion.funnel.advisor_filter_label",
            )}
            placeholder={translate(
              "crm.dashboard.direccion.funnel.filter_all_option",
            )}
            value={advisorParam ?? ""}
            options={advisorOptions}
            onChange={(value) => setParam(ADVISOR_PARAM, value)}
          />
        ) : null}
      </div>

      {error ? (
        <ListErrorState
          message={translate("crm.common.list_error")}
          onRetry={() => refetch()}
        />
      ) : isPending || !metrics ? (
        <ListRowsSkeleton rows={5} columns={2} />
      ) : isEmpty ? (
        <ListEmptyState
          title={translate("crm.dashboard.direccion.funnel.empty_title")}
          description={translate(
            "crm.dashboard.direccion.funnel.empty_description",
          )}
        />
      ) : (
        <>
          <div className="flex flex-col gap-3">
            {metrics.stages.map((stage) => (
              <FunnelStageRow
                key={stage.stage_key}
                label={stageLabelByKey.get(stage.stage_key) ?? stage.stage_key}
                stage={stage}
                maxDurationHours={maxDurationHours}
                translate={translate}
              />
            ))}
          </div>

          {isDireccion ? (
            <DireccionOnlyMetrics metrics={metrics} translate={translate} />
          ) : null}

          {narrative ? <p className="text-base">{narrative}</p> : null}
        </>
      )}
    </section>
  );
};
